import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Store, Gem, Package, TrendingUp, Loader2, ArrowLeft, Info, Sparkles,
} from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import ItemCard from '../components/ItemCard';
import { Sheet, SkeletonList, Empty, SectionHead } from '../components/ui';
import { rarityOf, SLOT_THEME, themeOf } from '../lib/classTheme';

export default function ItemShop() {
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();
  const { user, hero, load } = useAuth();
  const [slot, setSlot] = useState(sp.get('slot') || 'weapon');
  const [preview, setPreview] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['itemShop'],
    queryFn: () => api.get('/inventory/shop').then(r => r.data),
  });

  const buy = useMutation({
    mutationFn: ({ itemId, rarity }) => api.post('/inventory/buy', { itemId, rarity }),
    onSuccess: ({ data: d }) => {
      toast.success(`✨ ${d.name} acquired! −${d.price} points`, { duration: 4000 });
      setPreview(null);
      qc.invalidateQueries(['itemShop']);
      qc.invalidateQueries(['inventory']);
      qc.invalidateQueries(['hero']);
      load();
    },
    onError: e => toast.error(e.message),
  });

  useEffect(() => { setSp(slot ? { slot } : {}, { replace: true }); }, [slot]);

  const t = themeOf(hero?.class_key);
  const slotItems = data?.catalog?.[slot] || [];
  const balance = data?.balance ?? user?.points_balance ?? 0;

  if (isLoading) return <SkeletonList n={4} h="h-40" />;

  return (
    <div className="space-y-4 animate-fade-up">
      {/* ═══ HEADER ═══ */}
      <div className="card overflow-hidden">
        <div className={`p-5 bg-gradient-to-br ${t.gradient} text-white relative`}>
          <motion.div className="absolute inset-0 opacity-25"
            style={{ background: 'radial-gradient(circle at 25% 30%, white, transparent 55%)' }}
            animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 6, repeat: Infinity }} />

          <div className="relative flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 grid place-items-center shrink-0">
              <Store size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-2xl font-extrabold">Gear Shop</h1>
              <p className="text-white/85 text-sm">
                {t.icon} {t.name} equipment · 1 point = 1 {'{'}currency{'}'} spent
              </p>
            </div>
          </div>

          <div className="relative mt-4 flex items-center justify-between bg-white/15
            backdrop-blur rounded-2xl px-4 py-2.5">
            <span className="text-xs font-black uppercase tracking-wide opacity-85">
              Your balance
            </span>
            <span className="font-display text-2xl font-extrabold flex items-center gap-1.5">
              <Gem size={18} /> {compact(balance)}
            </span>
          </div>
        </div>

        <div className="p-3">
          <Link to="/inventory" className="btn-ghost btn-sm w-full">
            <Package size={14} /> Go to inventory
          </Link>
        </div>
      </div>

      {/* ═══ HOW TO EARN ═══ */}
      {balance < 300 && (
        <div className="card p-4 bg-brand-50 border-brand-200">
          <p className="font-bold text-sm text-brand-800 flex items-center gap-1.5">
            <Info size={14} /> Need more points?
          </p>
          <ul className="text-xs text-brand-700 font-semibold mt-2 space-y-1">
            <li>🛒 Shop at local producers — <b>1 point per unit spent</b></li>
            <li>🤝 Invite friends — <b>7% of everything they earn</b></li>
            <li>🧠 Play trivia — up to <b>90 points</b> per round</li>
            <li>⚔️ Win arena duels — <b>60 points</b> per victory</li>
          </ul>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Link to="/shops" className="btn-primary btn-sm">Find shops</Link>
            <Link to="/trivia" className="btn-ghost btn-sm">Play trivia</Link>
          </div>
        </div>
      )}

      {/* ═══ SLOT TABS ═══ */}
      <div className="flex gap-1 bg-white p-1 rounded-xl border border-ink-100
        overflow-x-auto no-scrollbar sticky top-14 z-20">
        {Object.entries(SLOT_THEME).map(([k, s]) => (
          <button key={k} onClick={() => setSlot(k)}
            className={`flex-1 min-w-[68px] flex flex-col items-center gap-0.5 py-2 rounded-lg
              text-[11px] font-bold transition
              ${slot === k ? 'bg-ink-900 text-white' : 'text-ink-500'}`}>
            <span className="text-base">{s.icon}</span>
            {s.label}
          </button>
        ))}
      </div>

      {/* ═══ SLOT INFO ═══ */}
      <div className="flex items-center gap-2 px-1">
        <span className="text-xl">{SLOT_THEME[slot].icon}</span>
        <div>
          <p className="font-bold text-sm">{SLOT_THEME[slot].label}</p>
          <p className="text-[11px] text-ink-400 font-semibold">{SLOT_THEME[slot].hint}</p>
        </div>
      </div>

      {/* ═══ ITEMS ═══ */}
      {!slotItems.length ? (
        <Empty icon={Store} title="Not available yet"
          sub="This gear is still being crafted. Check back soon!" />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {slotItems.map((it, i) => {
            const r = rarityOf(it.rarity);
            return (
              <motion.div key={`${it.itemId}-${it.rarity}`}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}>
                <ItemCard item={it} owned={it.owned} affordable={it.affordable}
                  showPrice onClick={() => setPreview(it)} />
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ═══ RARITY LEGEND ═══ */}
      <div className="card p-3.5">
        <p className="label">Rarity tiers</p>
        <div className="space-y-1.5">
          {['common', 'uncommon', 'rare', 'epic', 'legendary'].map(k => {
            const r = rarityOf(k);
            const sample = slotItems.find(x => x.rarity === k);
            return (
              <div key={k} className="flex items-center gap-2.5">
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: r.hex }} />
                <span className="text-xs font-black w-20" style={{ color: r.hex }}>{r.label}</span>
                <div className="flex-1 h-1.5 rounded-full bg-ink-100 overflow-hidden">
                  <div className="h-full rounded-full"
                    style={{ width: `${r.tier * 20}%`, background: r.hex }} />
                </div>
                {sample && (
                  <span className="text-[10px] font-bold text-ink-400 w-20 text-right">
                    💎{compact(sample.price)} · +{sample.totalStats}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-ink-400 font-semibold mt-2.5">
          Higher rarity = better visual design and much stronger stats.
          Sell any item back for 40% of its price.
        </p>
      </div>

      {/* ═══ PREVIEW SHEET ═══ */}
      <Sheet open={!!preview} onClose={() => setPreview(null)} title={preview?.name || ''}>
        {preview && <BuyPreview item={preview} balance={balance}
          busy={buy.isPending}
          onBuy={() => buy.mutate({ itemId: preview.itemId, rarity: preview.rarity })} />}
      </Sheet>
    </div>
  );
}

/* ============================================================
   BUY PREVIEW
   ============================================================ */
function BuyPreview({ item, balance, onBuy, busy }) {
  const r = rarityOf(item.rarity);
  const s = SLOT_THEME[item.slot];
  const after = balance - item.price;

  return (
    <div className="space-y-4">
      <div className="relative rounded-2xl overflow-hidden border-2"
        style={{ borderColor: r.hex, boxShadow: r.shadowCss }}>
        <div className="absolute inset-0 opacity-10"
          style={{ background: `radial-gradient(circle at 50% 40%, ${r.hex}, transparent 70%)` }} />
        {item.image
          ? <img src={item.image} alt="" className="relative w-full aspect-square object-cover" />
          : <div className="relative w-full aspect-square grid place-items-center text-5xl opacity-30">
              {item.icon}</div>}

        {r.tier >= 4 && (
          <motion.div className="absolute inset-0 pointer-events-none"
            style={{ background: `linear-gradient(115deg, transparent 40%, ${r.hex}44 50%, transparent 60%)` }}
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 1.5 }} />
        )}
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">{s.icon}</span>
          <span className="chip" style={{ background: `${r.hex}22`, color: r.hex }}>{r.label}</span>
          <span className="chip-ink">{s.label}</span>
        </div>
        <h3 className="font-display text-xl font-extrabold">{item.name}</h3>
        {item.description && <p className="text-sm text-ink-500 mt-1">{item.description}</p>}
      </div>

      {item.stats && Object.keys(item.stats).length > 0 && (
        <div className="card p-3.5">
          <div className="flex items-center justify-between mb-2.5">
            <p className="label !mb-0">Stat bonuses</p>
            <span className="chip-brand">
              <TrendingUp size={9} /> +{item.totalStats}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(item.stats).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-ink-50">
                <span className="text-[11px] font-semibold text-ink-600 capitalize">
                  {k.replace('_', '')}
                </span>
                <span className="text-sm font-black" style={{ color: r.hex }}>+{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card p-3.5 space-y-2">
        <Row l="Price" v={`💎 ${compact(item.price)}`} bold />
        <Row l="Your balance" v={`💎 ${compact(balance)}`} />
        <div className="h-px bg-ink-100" />
        <Row l="After purchase"
          v={`💎 ${compact(Math.max(0, after))}`}
          color={after < 0 ? 'text-red-500' : 'text-mint-600'} bold />
      </div>

      {item.owned ? (
        <div className="p-3 rounded-xl bg-mint-400/10 text-center">
          <p className="font-bold text-sm text-mint-700">✓ You already own this item</p>
          <Link to="/inventory" className="btn-ghost btn-sm mt-2">Go to inventory</Link>
        </div>
      ) : !item.available ? (
        <div className="p-3 rounded-xl bg-ink-100 text-center">
          <p className="font-bold text-sm text-ink-500">Not available yet</p>
        </div>
      ) : after < 0 ? (
        <div className="space-y-2">
          <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-center">
            <p className="font-bold text-sm text-red-600">
              You need {compact(item.price - balance)} more points
            </p>
          </div>
          <Link to="/shops" className="btn-primary w-full">Shop local to earn points</Link>
        </div>
      ) : (
        <button onClick={onBuy} disabled={busy} className="btn-primary w-full py-3 font-black"
          style={{ boxShadow: r.shadowCss }}>
          {busy
            ? <><Loader2 size={16} className="animate-spin" /> Purchasing…</>
            : <><Sparkles size={16} /> Buy for 💎{compact(item.price)}</>}
        </button>
      )}
    </div>
  );
}

function Row({ l, v, bold, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${bold ? 'font-bold' : 'font-semibold text-ink-500'}`}>{l}</span>
      <span className={`${bold ? 'font-black' : 'font-bold'} ${color || ''}`}>{v}</span>
    </div>
  );
}